// HangTagFinishing — stringing (method → string swatch → knot) and eyelets
// (size → plated finish) pickers for hang-tag products. State lives in
// `config.hang_tag_finishing = { stringing: {method, string_code, knot}, eyelets: {size, finish} }`.
import { useState } from "react";
import { Check, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { SelectRow } from "./configuratorControls";
import { money } from "../lib/api";

const NONE = "none";

export default function HangTagFinishing({ product, config, updateConfig }) {
  const cfg = product.hang_tag_finishing;
  const [pickerOpen, setPickerOpen] = useState(false);
  if (!cfg?.enabled) return null;
  const sel = config.hang_tag_finishing || {};
  const s = sel.stringing || {};
  const e = sel.eyelets || {};
  const f = sel.fiber || {};
  const c = sel.corner || {};
  const scfg = cfg.stringing || {};
  const ecfg = cfg.eyelets || {};
  const fcfg = cfg.fiber || {};
  const ccfg = cfg.corner || {};
  const set = (patch) => updateConfig({ hang_tag_finishing: { ...sel, ...patch } });
  const chosenString = (scfg.options || []).find((o) => o.code === s.string_code);
  const feeLine = (c) => `${money(c.setup_fee)} setup + ${money(c.per_unit)} / tag`;

  return (
    <>
      {scfg.enabled !== false && (
      <div className="space-y-2" data-testid="hang-tag-stringing">
        <SelectRow
          label="Stringing"
          value={s.method || NONE}
          onChange={(v) => {
            if (v === NONE) return set({ stringing: null });
            set({ stringing: { method: v, string_code: null, knot: (scfg.knots || [])[0] } });
            setPickerOpen(true);
          }}
          options={[
            { value: NONE, label: "No string — hole only" },
            ...(scfg.methods || []).map((m) => ({ value: m.key, label: m.label })),
          ]}
          testid="select-stringing-method"
        />
        {s.method && (
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className={`w-full flex items-center gap-3 border p-2 text-left transition-colors ${
              chosenString ? "border-kolt-orange bg-kolt-paper-soft" : "border-dashed border-kolt-orange bg-white hover:bg-kolt-paper-soft"
            }`}
            data-testid="stringing-pick-btn"
          >
            {chosenString ? (
              <>
                <img src={chosenString.image} alt="" className="h-6 w-24 object-cover border border-slate-200" />
                <span className="text-sm">
                  <span className="font-mono font-bold">#{chosenString.code}</span> · {chosenString.name}
                </span>
                <span className="ml-auto font-mono text-[10px] tracking-widest uppercase text-kolt-orange">Change</span>
              </>
            ) : (
              <span className="text-sm font-bold text-kolt-orange">Choose your string / cord →</span>
            )}
          </button>
        )}
        {chosenString && (
          <>
            <SelectRow
              label="Knot style"
              value={s.knot || (scfg.knots || [])[0]}
              onChange={(v) => set({ stringing: { ...s, knot: v } })}
              options={(scfg.knots || []).map((k) => ({ value: k, label: k }))}
              testid="select-knot"
            />
            <img src={scfg.knot_diagram} alt="Knot styles: Knotted, Looped, Wired, Loop Knotted"
                 className="w-full border border-slate-200 bg-white" data-testid="knot-diagram" />
            <p className="text-[11px] text-slate-500" data-testid="stringing-fee">Stringing: {feeLine(scfg)}</p>
          </>
        )}
      </div>
      )}

      {ecfg.enabled !== false && (
      <div className="space-y-2" data-testid="hang-tag-eyelets">
        <SelectRow
          label="Eyelets"
          value={e.finish ? "yes" : NONE}
          onChange={(v) => set({ eyelets: v === NONE ? null : { size: (ecfg.sizes || [])[1], finish: (ecfg.finishes || [])[0]?.key } })}
          options={[{ value: NONE, label: "No eyelet — plain drilled hole" }, { value: "yes", label: "Add rolled-flange eyelets" }]}
          testid="select-eyelets"
        />
        {e.finish && (
          <>
            <SelectRow
              label="Eyelet size"
              value={e.size}
              onChange={(v) => set({ eyelets: { ...e, size: v } })}
              options={(ecfg.sizes || []).map((z) => ({ value: z, label: z }))}
              testid="select-eyelet-size"
            />
            <div className="text-xs font-bold uppercase tracking-wider text-slate-600">Finish / plating</div>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5" data-testid="eyelet-finish-grid">
              {(ecfg.finishes || []).map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => set({ eyelets: { ...e, finish: f.key } })}
                  title={f.name}
                  data-testid={`eyelet-finish-${f.key}`}
                  className={`relative bg-white border-2 p-1 text-center transition-colors ${
                    e.finish === f.key ? "border-kolt-orange" : "border-slate-200 hover:border-kolt-orange"
                  }`}
                >
                  <img src={f.image} alt={f.name} className="w-full aspect-square object-contain" />
                  <div className="text-[9px] leading-tight text-slate-600 truncate">{f.name}</div>
                  {e.finish === f.key && <Check className="absolute top-0.5 right-0.5 w-3 h-3 text-kolt-orange" />}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-500" data-testid="eyelets-fee">Eyelets: {feeLine(ecfg)}</p>
          </>
        )}
      </div>
      )}

      {fcfg.enabled !== false && (
      <div className="space-y-2" data-testid="hang-tag-fiber">
        <SelectRow
          label="Fiber reinforcing"
          value={f.sides || NONE}
          onChange={(v) => set({ fiber: v === NONE ? null : { sides: v, hole: f.hole || (fcfg.holes || [])[0] } })}
          options={[
            { value: NONE, label: "No reinforcing patch" },
            ...(fcfg.sides || []).map((x) => ({ value: x.key, label: `Reinforcing patch — ${x.name.toLowerCase()}` })),
          ]}
          testid="select-fiber-sides"
        />
        {f.sides && (
          <>
            <SelectRow
              label="Hole size"
              value={f.hole}
              onChange={(v) => set({ fiber: { ...f, hole: v } })}
              options={(fcfg.holes || []).map((h) => ({ value: h, label: h }))}
              testid="select-fiber-hole"
            />
            <img src={fcfg.chart} alt="Fiber reinforcing patch on a hang tag"
                 className="w-full border border-slate-200 bg-white" data-testid="fiber-diagram" />
            <p className="text-[11px] text-slate-500" data-testid="fiber-fee">
              Patch O.D. {fcfg.patch_od} · Fiber reinforcing: {feeLine(fcfg)}
            </p>
          </>
        )}
      </div>
      )}

      {ccfg.enabled !== false && (
      <div className="space-y-2" data-testid="hang-tag-corner">
        <SelectRow
          label="Corner cutting — standard 45°"
          value={c.style || NONE}
          onChange={(v) => set({ corner: v === NONE ? null : { style: v, clip: c.clip || (ccfg.clips || [])[1] } })}
          options={[
            { value: NONE, label: "Square corners (no clip)" },
            ...(ccfg.styles || []).map((x) => ({ value: x.key, label: `Clip ${x.name.toLowerCase()}` })),
          ]}
          testid="select-corner-style"
        />
        {c.style && (
          <>
            <SelectRow
              label="Clip size (along the edge)"
              value={c.clip}
              onChange={(v) => set({ corner: { ...c, clip: v } })}
              options={(ccfg.clips || []).map((h) => ({ value: h, label: h }))}
              testid="select-corner-clip"
            />
            <div className="flex items-center gap-3 border border-slate-200 bg-white p-2">
              <img src={ccfg.chart} alt="Clipped corner — standard 45 degrees, 1.5 inch max"
                   className="w-24 h-24 object-contain" data-testid="corner-diagram" />
              <p className="text-[11px] text-slate-500" data-testid="corner-fee">
                45° clip, up to 1.5" max along each edge. Corner cutting: {feeLine(ccfg)}
              </p>
            </div>
          </>
        )}
      </div>
      )}

      <StringPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        cfg={scfg}
        method={s.method}
        selected={s.string_code}
        onSelect={(code) => { set({ stringing: { ...s, string_code: code } }); setPickerOpen(false); }}
      />
    </>
  );
}

function StringPicker({ open, onClose, cfg, method, selected, onSelect }) {
  const options = (cfg.options || []).filter((o) => o.method === method);
  const label = (cfg.methods || []).find((m) => m.key === method)?.label || "String & Cords";
  const sections = [["Non-Elastic", options.filter((o) => !o.elastic)], ["Elastic", options.filter((o) => o.elastic)]];
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="!max-w-3xl w-[95vw] max-h-[90vh] p-0 overflow-hidden flex flex-col" data-testid="string-picker-dialog">
        <DialogHeader className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <DialogTitle className="font-display text-xl font-black text-kolt-inkwell flex items-center justify-between">
            <span>{label}</span>
            <button onClick={onClose} className="text-slate-500 hover:text-slate-900" aria-label="Close"><X className="w-5 h-5" /></button>
          </DialogTitle>
          <p className="text-sm text-slate-500">Pick the string by its reference code — we string every tag before it ships.</p>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {sections.filter(([, items]) => items.length).map(([title, items]) => (
            <div key={title}>
              <div className="overline mb-2">{title} · {items.length}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {items.map((o) => (
                  <button
                    key={o.code}
                    type="button"
                    onClick={() => onSelect(o.code)}
                    data-testid={`string-swatch-${o.code}`}
                    className={`flex items-center gap-3 bg-white border-2 p-2 text-left transition-colors ${
                      selected === o.code ? "border-kolt-orange" : "border-slate-200 hover:border-kolt-orange"
                    }`}
                  >
                    <span className="font-mono text-xs font-bold w-7 text-slate-500">#{o.code}</span>
                    <img src={o.image} alt="" className="h-7 flex-1 min-w-0 object-cover border border-slate-100" />
                    <span className="text-xs w-36 truncate" title={o.name}>{o.name}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div data-testid="string-boards">
            <div className="overline mb-2">Physical string boards — visual reference</div>
            <div className="grid grid-cols-3 gap-2">
              {["a", "b", "c"].map((b) => (
                <a key={b} href={`/hang-tags/boards/string-board-${b}.jpg`} target="_blank" rel="noreferrer"
                   className="block border border-slate-200 hover:border-kolt-orange transition-colors"
                   data-testid={`string-board-${b}`}>
                  <img src={`/hang-tags/boards/string-board-${b}.jpg`} alt={`Kolt Printing string board ${b.toUpperCase()}`}
                       loading="lazy" className="w-full aspect-square object-cover" />
                  <div className="text-center font-mono text-[9px] tracking-widest uppercase text-slate-500 py-1">Board {b.toUpperCase()}</div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
